import dayjs from 'dayjs';
import { DASHBOARD_METRIC_VALUE_STATUS } from './dashboardContract';

export const DASHBOARD_VALUE_HISTORY_PAGE_SIZE = 10;
export const DASHBOARD_VALUE_REMOVAL_REASON_MAX_LENGTH = 500;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const hasValidCalendarDate = (value) => {
  if (!ISO_DATE_PATTERN.test(value ?? '')) return false;

  const [year, month, day] = value.split('-').map(Number);
  const candidate = new Date(Date.UTC(year, month - 1, day));

  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
};

export const createDashboardValueHistoryRange = (referenceDate = dayjs()) => {
  const today = dayjs(referenceDate);

  return {
    fromDate: today.subtract(29, 'day').format('YYYY-MM-DD'),
    toDate: today.format('YYYY-MM-DD')
  };
};

export const validateDashboardValueHistoryRange = ({ fromDate, toDate }) => {
  if (!hasValidCalendarDate(fromDate)) {
    return 'Enter a valid from date.';
  }
  if (!hasValidCalendarDate(toDate)) {
    return 'Enter a valid to date.';
  }
  if (fromDate > toDate) {
    return 'The from date must be on or before the to date.';
  }

  const inclusiveDays = dayjs(toDate).diff(dayjs(fromDate), 'day') + 1;
  if (inclusiveDays > 366) {
    return 'Choose a date range of 366 days or less.';
  }

  return null;
};

export const buildDashboardValueRows = (
  seriesMetrics = [],
  configuredMetrics = []
) => {
  const configuredById = new Map(
    configuredMetrics.map((metric) => [metric.metricDefinitionId, metric])
  );

  return seriesMetrics
    .filter(
      (metric) =>
        !metric.fieldSource &&
        !configuredById.get(metric.metricDefinitionId)?.fieldSource
    )
    .flatMap((seriesMetric) => {
      const metric = {
        ...seriesMetric,
        ...configuredById.get(seriesMetric.metricDefinitionId)
      };

      return (seriesMetric.points ?? []).map((point) => ({
        ...point,
        metric,
        metricDefinitionId: seriesMetric.metricDefinitionId
      }));
    })
    .sort((left, right) => {
      const dateOrder = right.effectiveDate.localeCompare(left.effectiveDate);
      if (dateOrder !== 0) return dateOrder;

      const displayOrder =
        Number(left.metric.displayOrder) - Number(right.metric.displayOrder);
      if (displayOrder !== 0) return displayOrder;

      return left.metric.label.localeCompare(right.metric.label);
    });
};

export const paginateDashboardValueRows = (
  rows = [],
  page = 1,
  pageSize = DASHBOARD_VALUE_HISTORY_PAGE_SIZE
) => rows.slice((page - 1) * pageSize, page * pageSize);

export const validateDashboardValueRemovalReason = (reason) => {
  const normalized = String(reason ?? '').trim();
  if (!normalized) return 'A removal reason is required.';
  if (normalized.length > DASHBOARD_VALUE_REMOVAL_REASON_MAX_LENGTH) {
    return (
      'Removal reason cannot exceed ' +
      DASHBOARD_VALUE_REMOVAL_REASON_MAX_LENGTH +
      ' characters.'
    );
  }
  return null;
};

export const buildDashboardValueRevisionMessage = (revision) => {
  if (
    revision?.valueStatus === DASHBOARD_METRIC_VALUE_STATUS.PENDING_APPROVAL
  ) {
    return (
      revision.label +
      ' update was submitted for approval. The current approved value remains visible until the update is approved.'
    );
  }

  return revision?.label + ' was updated and is now visible on the dashboard.';
};
